import PropTypes from 'prop-types';

export const cellShape = PropTypes.shape({
  class: PropTypes.string,
  html: PropTypes.string,
  id: PropTypes.string
});

export const rowShape = PropTypes.shape({
  class: PropTypes.string,
  cells: PropTypes.arrayOf(cellShape).isRequired
});

export const overrideShape = PropTypes.shape({
  bodyRows: PropTypes.arrayOf(rowShape).isRequired,
  headerRows: PropTypes.arrayOf(rowShape)
});

export default class TableOverrideHelper {
  constructor(defaults, { newColumn, newRow }) {
    this.defaults = defaults;
    this.newColumn = newColumn;
    this.newRow = newRow;
  }

  getColumnCount(override) {
    return getColumnCount(override, this.defaults);
  }

  getBodyRowCount(override) {
    return getBodyRowCount(override, this.defaults);
  }

  getCellKey(cell, rowIndex, columnIndex) {
    if (cell.hasOwnProperty('id')) {
      return cell.id;
    } else {
      return `${rowIndex}-${columnIndex}`;
    }
  }

  changeCellAt(section, rowIndex, columnIndex, html, override) {
    override = this._copyOverride(override);
    override[section][rowIndex].cells[columnIndex].html = html;
    return override;
  }

  resetCellAt(section, rowIndex, columnIndex, override) {
    override = this._copyOverride(override);
    delete override[section][rowIndex].cells[columnIndex].html;
    return override;
  }

  addColumnBefore(index, override) {
    override = this._copyOverride(override);
    index = index || 0;

    override.columns.splice(index, 0, { ...this.newColumn });

    forEachRow(override, (row) => {
      row.cells.splice(index, 0, buildCellOverride(this.newColumn));
    });

    return override;
  }

  addColumnAfter(index, override) {
    override = this._copyOverride(override);

    if (index == null) {
      index = this.getColumnCount(override) - 1;
    }

    override.columns.splice(index + 1, 0, { ...this.newColumn });

    forEachRow(override, (row) => {
      row.cells.splice(index + 1, 0, buildCellOverride(this.newColumn));
    });

    return override;
  }

  deleteColumnAt(index, override) {
    override = this._copyOverride(override);

    if (index == null) {
      index = this.getColumnCount(override) - 1;
    }

    override.columns.splice(index, 1);

    forEachRow(override, (row) => {
      row.cells.splice(index, 1);
    });

    return override;
  }

  swapColumns(index1, index2, override) {
    override = this._copyOverride(override);

    const column = override.columns[index1];
    override.columns[index1] = override.columns[index2];
    override.columns[index2] = column;

    forEachRow(override, (row) => {
      const cell = row.cells[index1];
      row.cells[index1] = row.cells[index2];
      row.cells[index2] = cell;
    });

    return override;
  }

  addRowBefore(index, override) {
    override = this._copyOverride(override);
    override.bodyRows.splice(index || 0, 0, buildRowFor(override, this.newRow));
    return override;
  }

  addRowAfter(index, override) {
    override = this._copyOverride(override);

    if (index == null) {
      index = getBodyRowCount(override) - 1;
    }

    override.bodyRows.splice(index + 1, 0, buildRowFor(override, this.newRow));
    return override;
  }

  deleteRowAt(index, override) {
    override = this._copyOverride(override);

    if (index == null) {
      index = getBodyRowCount(override) - 1;
    }

    override.bodyRows.splice(index, 1);
    return override;
  }

  swapRows(index1, index2, override) {
    override = this._copyOverride(override);

    const row = override.bodyRows[index1];
    override.bodyRows[index1] = override.bodyRows[index2];
    override.bodyRows[index2] = row;

    return override;
  }

  _copyOverride(override) {
    if (override) {
      return JSON.parse(JSON.stringify(override));
    } else {
      return buildOverride(this.defaults);
    }
  }
}

function buildOverride({ columns, bodyRows, headerRows }) {
  return {
    columns: columns.map(column => ({ ...column })),
    bodyRows: bodyRows.map(buildRowOverride),
    headerRows: headerRows ? headerRows.map(buildRowOverride) : null
  };
}

function buildRowOverride(row) {
  return {
    class: row.class,
    cells: row.cells.map(cell => ({ ...cell })),
    style: row.style
  };
}

function buildCellOverride(newColumn) {
  return { ...newColumn, html: '' };
}

function buildRowFor(override, newRow) {
  const cells = [];
  for (let i = 0; i < getColumnCount(override); i++) {
    const cell = buildCellOverride();
    cells.push(cell);

    const column = override.columns[i];
    cell.class = column.class;
    cell.style = column.style;
  }

  return { ...newRow, cells };
}

function forEachRow(override, callback) {
  for (const section of ['bodyRows', 'headerRows']) {
    const rows = override[section];
    if (!rows) {
      continue;
    }

    for (const row of rows) {
      callback(row);
    }
  }
}

function getColumnCount(override, defaults) {
  return (override || defaults).columns.length;
}

function getBodyRowCount(override, defaults) {
  const bodyRows = (override || defaults).bodyRows;
  return bodyRows.length;
}
